<template>
  <div>
    <form class="form" @submit.prevent="">
      <div class="pb-8 form-fields">
        <div class="grid grid-cols-2 gap-6">
          <VFormSelect
            label="Product*"
            placeholder="Find a product by name..."
            :options="products"
            optionKey="netsuite_name"
            :error="errors.product"
            :showFilterInput="true"
            :classes="{ options: 'max-h-74' }"
            :reset="!fieldsData.product"
            @onSelect="value => handleSetField('product', value)"
            @filter="text => $emit('filter', text)"
          />
          <VFormInput
            label="Serial Number*"
            placeholder="Enter serial number"
            :error="errors.serial_number"
            :reset="!fieldsData.serial_number"
            @update="value => handleSetField('serial_number', value)"
          />
        </div>

        <div class="grid grid-cols-2 gap-6">
          <VFormDatePicker
            label="Select a Date*"
            @pick="date => handleSetField('purchase_date', date)"
          />
          <VFormSelect
            label="Chroma Tier"
            placeholder="Select a Chroma Tier"
            :options="chromaTiers"
            :error="errors.tier"
            :reset="!fieldsData.tier"
            @onSelect="value => handleSetField('tier', value)"
          />
        </div>
      </div>
    </form>

    <!-- Owned products table -->
    <div class="border-none shared-table-container">
      <div class="shared-table-innermost-container">
        <table class="shared-table">
          <thead>
            <tr class="bg-transparent shared-table-headings">
              <th
                v-for="(header, index) in $options.tableHeaders"
                :key="`heading-${index}`"
                class="shared-table-headings-heading text-ricoma-gray-700"
              >
                <div class="flex items-center justify-center">
                  {{ header }}
                </div>
              </th>
              <th class="shared-table-headings-heading text-ricoma-gray-700">
                <div class="flex items-center justify-center">Actions</div>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="customerProducts.length === 0">
              <td colspan="5">
                <div class="flex items-center justify-center py-6">-- EMPTY --</div>
              </td>
            </tr>
            <tr
              v-else
              v-for="(data, index) in customerProducts"
              :key="`data-${index}`"
              class="shared-table-cells odd:bg-ricoma-gray-200"
            >
              <td class="shared-table-cells-cell">{{ data.netsuite_name }}</td>
              <td class="shared-table-cells-cell">{{ data.pivot.serial_number || ' - ' }}</td>
              <td class="shared-table-cells-cell">{{ data.pivot.tier || ' - ' }}</td>
              <td class="shared-table-cells-cell">
                {{
                  (data.pivot.purchase_date && formatPurchaseDate(data.pivot.purchase_date)) ||
                  ' - '
                }}
              </td>
              <td class="shared-table-cells-cell">
                <div class="flex justify-center">
                  <button
                    class="shared-table-cells-button round delete"
                    @click="handleDeleteProduct(data.pivot.id)"
                  >
                    <VSvgIcon name="trash" class="h-5" />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- modal buttons -->
    <div class="form-buttons">
      <button class="form-buttons-cancel" type="button" @click="() => $emit('close')">
        Cancel
      </button>
      <button
        type="button"
        class="form-buttons-proceed"
        :disabled="formLoading"
        @click="handleSubmit(handleProceed)()"
      >
        <BeatLoader
          v-if="formLoading"
          color="#FAFAFA"
          :size="0.875"
          sizeUnit="rem"
          class="flex items-center"
        />
        <span v-else>Add</span>
      </button>
    </div>
  </div>
</template>

<script>
import { format } from 'date-fns';

import { formMixin } from '@/mixins';

import VSvgIcon from '@/components/base/VSvgIcon';
import VFormInput from '@/components/base/VFormInput';
import VFormSelect from '@/components/base/VFormSelect';
import VFormDatePicker from '@/components/base/VFormDatePicker';

const TABLE_HEADERS = ['Product Name', 'Serial Number', 'Chroma Tier', 'Purchase Date'];

export default {
  name: 'CustomerProductsForm',
  components: { VSvgIcon, VFormInput, VFormSelect, VFormDatePicker },
  mixins: [
    formMixin(
      {
        product: null,
        serial_number: '',
        purchase_date: '',
        tier: '',
      },
      ['tier'],
      ['product'],
    ),
  ],
  props: {
    chromaTiers: {
      type: Array,
      required: false,
      default: () => [],
    },
    products: {
      type: Array,
      required: false,
      default: () => [],
    },
    customerProducts: {
      type: Array,
      required: false,
      default: () => [],
    },
    customerId: {
      type: Number,
      required: true,
    },
  },
  methods: {
    formatPurchaseDate(date) {
      return format(new Date(date), 'MMM do, yyyy');
    },
    handleProceed(data) {
      this.handleAddProduct(data);
      this.handleResetFields();
      this.handleResetErrors();
    },
    async handleAddProduct({ product, purchase_date, ...rest }) {
      const data = {
        product_id: product.id,
        purchase_date: format(new Date(purchase_date), 'yyyy/MM/dd'),
        ...rest,
      };
      const res = await this.$http.post(`admin/customers/${this.customerId}/products`, data);
      this.$emit('update', res.data);
    },
    async handleDeleteProduct(id) {
      const res = await this.$http.delete(`admin/customers/${this.customerId}/products/${id}`);
      this.$emit('update', res.data);
    },
  },
  created() {
    this.$options.tableHeaders = TABLE_HEADERS;
  },
};
</script>
